import { memo, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Box, BoxProps, Divider, IconButton, IconButtonProps, styled, Typography, useTheme } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { ITicket } from '@tymely/atoms';
import ResizeIcon from '@tymely/components/Icons/Resize';

import TicketNotes from './TicketNotes';
import TicketsHistory from './TicketsHistory';

const DrawerContainer = styled(Box)(
    ({ theme }) => `
    display: flex;
    flex-direction: column;
    width: 0;
    flex: 1;
    background-color: ${theme.palette.common.white};
    padding-top: ${theme.spacing(4)};
`,
);

const DrawerContent = memo(
    ({ children, onClose, ...props }: { children?: ReactNode; onClose: () => void } & BoxProps) => {
        const theme = useTheme();
        return (
            <DrawerContainer {...props}>
                <Box component="header" width={1} minHeight={theme.spacing(4)} position="relative">
                    <Box
                        role="button"
                        display="inline-flex"
                        alignItems="center"
                        position="absolute"
                        right={theme.spacing(-2)}
                        bottom={0}
                        sx={{ cursor: 'pointer' }}
                        onClick={onClose}
                    >
                        <Typography color="text.disabled" variant="subtitle2" marginRight={-1.5} sx={{ zIndex: 1 }}>
                            Collapse
                        </Typography>
                        <Box
                            display="inline-flex"
                            alignItems="center"
                            width={44}
                            height={44}
                            borderRadius="50%"
                            sx={{ backgroundColor: theme.palette.common.white }}
                        >
                            <ArrowBackIosIcon
                                sx={{ position: 'absolute', right: 4, fontSize: '1rem', color: '#6E6D99' }}
                            />
                        </Box>
                    </Box>
                </Box>
                {children}
            </DrawerContainer>
        );
    },
);
DrawerContent.displayName = 'DrawerContent';

const StyledIconButton = styled(IconButton)`
    width: 26px;
    height: 26px;
    background-color: var(--ty-palette-common-white);
    cursor: pointer;
`;

const ResizeButton = styled((props: IconButtonProps) => (
    <StyledIconButton {...props}>
        <ResizeIcon />
    </StyledIconButton>
))`
    position: absolute;
    transform: translateY(-50%);
    right: 32px;
    border: 1px solid var(--ty-palette-divider);
    z-index: 1;
`;

const TicketHistoryDrawer = memo(
    (props: { open: boolean; onClose: () => void; onRecentTickets: (tickets: ITicket[]) => void }) => {
        const [[startY, startH], setResize] = useState<[number?, number?]>([]);
        const notesRef = useRef<HTMLUListElement>(null);

        const onResizeStart = useCallback((event: MouseEvent) => {
            setResize([event.clientY, notesRef.current?.clientHeight]);
        }, []);

        const onResize = useCallback(
            (event: MouseEvent) => {
                if (startY === undefined || startH === undefined) return;
                notesRef.current!.style.height = startH + startY - event.clientY + 'px';
            },
            [startY, startH],
        );

        const onResizeStop = useCallback(() => {
            setResize([]);
        }, []);

        useEffect(() => {
            document.addEventListener('mouseup', onResizeStop);
            return () => document.removeEventListener('mouseup', onResizeStop);
        }, []);

        return (
            <DrawerContent sx={{ display: props.open ? 'block' : 'none' }} onClose={props.onClose}>
                <Box
                    display="flex"
                    flexDirection="column"
                    height={1}
                    onMouseMove={(e) => onResize(e as unknown as MouseEvent)}
                >
                    <TicketsHistory onRecentTickets={props.onRecentTickets} />
                    <Box position="relative">
                        <Divider sx={{ borderColor: 'var(--ty-palette-divider)' }} />
                        <ResizeButton onMouseDown={(e) => onResizeStart(e as unknown as MouseEvent)} />
                    </Box>
                    <TicketNotes ref={notesRef} />
                </Box>
            </DrawerContent>
        );
    },
);

TicketHistoryDrawer.displayName = 'TicketHistoryDrawer';
export default TicketHistoryDrawer;
